import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Loader2, ChevronLeft, ShieldCheck, RefreshCw, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCrypto } from "../context/CryptoContext";
import { keystore } from "../lib/keystore";
import { getAttribution } from "../lib/attribution";
import { Crest } from "../components/Media";
import { DomainPicker } from "../components/DomainPicker";
import { TrustBadges } from "../components/TrustBadges";

const Field = ({ label, type, "data-testid": testId, ...props }) => {
  const [show, setShow] = useState(false);
  const isPw = type === "password";
  return (
    <label className="block">
      <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">{label}</span>
      <div className="relative mt-2">
        <input
          {...props}
          data-testid={testId}
          type={isPw ? (show ? "text" : "password") : type}
          className={`bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] transition-colors w-full font-body ${isPw ? "pr-9" : ""}`}
        />
        {isPw && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            data-testid={testId ? `${testId}-toggle` : "password-toggle"}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#14A89A] transition-colors"
          >
            {show ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
          </button>
        )}
      </div>
    </label>
  );
};

const Area = ({ label, helper, ...props }) => (
  <label className="block">
    <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">{label}</span>
    <textarea {...props} rows={3} className="mt-2 bg-transparent border border-white/15 rounded-none px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] transition-colors w-full font-body resize-none" />
    {helper && <span className="block mt-1.5 font-body text-[11px] text-zinc-600">{helper}</span>}
  </label>
);

// Tappable starter chips to beat the blank-page freeze on the application.
const ChipRow = ({ chips, onPick, testid }) => (
  <div className="flex flex-wrap gap-2 mt-2.5" data-testid={testid}>
    <span className="font-body text-[10px] text-zinc-600 self-center mr-0.5">Tap to start:</span>
    {chips.map((c) => (
      <button
        type="button"
        key={c}
        onClick={() => onPick(c)}
        data-testid={`${testid}-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        className="px-2.5 py-1 font-body text-[11px] border border-white/15 text-zinc-400 hover:text-white hover:border-[#14A89A]/60 transition-colors"
      >
        {c}
      </button>
    ))}
  </div>
);

// A shared link (e.g. /hq in the newsletter) remembered across sign in.
export const afterLogin = () => (sessionStorage.getItem("ss_after_login") === "/hq" ? "/hq" : "/feed");


export default function Gate() {
  const [stage, setStage] = useState("intro"); // intro | gate | login | apply
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [phrase, setPhrase] = useState(null);
  const [phraseAck, setPhraseAck] = useState(false);
  const [showPhraseConfirm, setShowPhraseConfirm] = useState(false);
  const [pendingSession, setPendingSession] = useState(null);

  // Deep link: /?invite=CODE or /?ref=CODE prefills + auto-advances if valid. /?key=CODE prefills only.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inv = params.get("invite") || params.get("ref");
    const keyOnly = params.get("key");
    if (inv) {
      const c = inv.trim().toUpperCase();
      setCode(c);
      setStage("gate");
      (async () => {
        try {
          const { data } = await api.post("/auth/verify-invite", { code: c });
          if (data.valid) { hydrateApply(c, data.prefill); setStage("apply"); }
        } catch { /* stay on gate with the code prefilled */ }
      })();
      return;
    }
    if (keyOnly) {
      setCode(keyOnly.trim().toUpperCase());
      setStage("gate");
    }
  }, []);
  const { setSession } = useAuth();
  const { buildKeysForRegister, resetWithRecovery } = useCrypto();
  const navigate = useNavigate();

  // login fields
  const [lEmail, setLEmail] = useState("");
  const [lPass, setLPass] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [pendingPriv, setPendingPriv] = useState(null);

  const completeLogin = (data) => {
    if (data.mfa_required) { setMfaToken(data.mfa_token); setError(""); setMfaCode(""); setStage("mfa"); return; }
    setSession(data.token, data.user);
    navigate(data.user.status === "approved" || data.user.role === "admin" ? afterLogin() : "/pending");
  };

  // forgot-password fields
  const [fEmail, setFEmail] = useState("");
  const [fPhrase, setFPhrase] = useState("");
  const [fNewPw, setFNewPw] = useState("");

  // apply fields
  const [form, setForm] = useState({
    name: "", email: "", password: "", domains: [],
    headline: "", bio: "", offering: "", seeking: "", link: "",
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [applyStep, setApplyStep] = useState(1); // apply is split into 2 short steps
  const [resumed, setResumed] = useState(false);
  const draftKey = (c) => `ss_apply_draft_${(c || "").toUpperCase()}`;

  // Restore a saved draft, else prefill from their invite request (they shouldn't retype).
  function hydrateApply(c, prefill) {
    let draft = null;
    try { draft = JSON.parse(localStorage.getItem(draftKey(c)) || "null"); } catch { /* ignore */ }
    if (draft && Date.now() - (draft.saved_at || 0) > 30 * 86400000) draft = null;
    if (draft) {
      const { step, saved_at, ...fields } = draft;
      setForm((f) => ({ ...f, ...fields, password: "" }));
      setApplyStep(1);
      setResumed(true);
      return;
    }
    setApplyStep(1);
    if (prefill) setForm((f) => ({ ...f, name: prefill.name || f.name, email: prefill.email || f.email, bio: prefill.creating || f.bio }));
  }

  // Autosave everything except the password, so a half-finished profile survives a closed tab.
  useEffect(() => {
    if (stage !== "apply" || !code) return;
    const { password, ...rest } = form;
    try { localStorage.setItem(draftKey(code), JSON.stringify({ ...rest, step: applyStep, saved_at: Date.now() })); } catch { /* ignore */ }
  }, [stage, code, form, applyStep]);

  const verify = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-invite", { code });
      if (data.valid) { hydrateApply(code, data.prefill); setStage("apply"); }
      else setError("That signal key is not recognized.");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doLogin = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: lEmail, password: lPass });
      completeLogin(data);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doMfaVerify = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code: mfaCode });
      if (pendingPriv) { try { await keystore.savePrivate(data.user.id, pendingPriv); } catch {} setPendingPriv(null); }
      setSession(data.token, data.user);
      navigate(data.user.status === "approved" || data.user.role === "admin" ? afterLogin() : "/pending");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { privateKeyJwk } = await resetWithRecovery(fEmail, fPhrase, fNewPw);
      const { data } = await api.post("/auth/login", { email: fEmail, password: fNewPw });
      if (data.mfa_required) { setPendingPriv(privateKeyJwk); completeLogin(data); return; }
      await keystore.savePrivate(data.user.id, privateKeyJwk);
      setSession(data.token, data.user);
      navigate(data.user.status === "approved" || data.user.role === "admin" ? afterLogin() : "/pending");
    } catch (e) {
      setError(e.response ? formatApiErrorDetail(e.response?.data?.detail) : (e.message || "Could not reset. Check your recovery phrase."));
    } finally { setLoading(false); }
  };

  const goToStep2 = () => {
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      setError("Please fill in your name, email and password."); return;
    }
    if (form.password.length < 8) { setError("Please choose a password of at least 8 characters."); return; }
    if (form.domains.length === 0) { setError("Choose at least one realm."); return; }
    api.post("/auth/apply-started", { code }).catch(() => {});
    setApplyStep(2);
  };

  const doApply = async (e) => {
    e.preventDefault(); setError("");
    if (form.password.length < 8) { setApplyStep(1); setResumed(true); return; }
    setLoading(true);
    try {
      const keys = await buildKeysForRegister(form.password);
      const attribution = getAttribution() || {};
      // Headline is a short, discoverable profile tagline. Since the dedicated
      // "what have you created?" field was merged into the story, derive it from
      // the first line/sentence of the story (member can edit it anytime).
      const story = form.bio.trim();
      const headline = ((story.split(/[.\n]/)[0] || story).trim()).slice(0, 120);
      const { data } = await api.post("/auth/register", {
        invite_code: code, email: form.email, password: form.password, name: form.name,
        crypto: keys.crypto,
        utm: attribution.utm || null,
        ref: attribution.ref || null,
        questionnaire: {
          domains: form.domains, headline,
          bio: story, offering: "", seeking: form.seeking, link: form.link,
        },
      });
      await keystore.savePrivate(data.user.id, keys.privateKeyJwk);
      try { localStorage.removeItem(draftKey(code)); } catch { /* ignore */ }
      setPendingSession({ token: data.token, user: data.user });
      setPhrase(keys.phrase);
      setStage("phrase");
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
      <img src="/logo-crest.png" alt="" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150vmin] max-w-none opacity-[0.24] pointer-events-none select-none" />
      {/* soft brand glow to lift the dark space (thumbnail-friendly, subtle) */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(62% 58% at 62% 42%, rgba(20,168,154,0.26) 0%, rgba(20,168,154,0.1) 40%, transparent 72%)" }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 via-transparent to-transparent" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="flex items-center justify-between gap-3 p-6 md:p-10">
          <div className="flex items-center gap-3">
            <Crest className="h-9 w-9" />
            <div className="leading-tight">
              <div className="font-heading text-sm tracking-tight text-white">SOVEREIGN</div>
              <div className="font-body text-[10px] tracking-[0.35em] text-[#14A89A]">SIGNAL</div>
            </div>
          </div>
          <a href="/security" data-testid="landing-nav-security" className="flex items-center gap-1.5 text-zinc-400 hover:text-[#14A89A] transition-colors font-body text-xs sm:text-sm">
            <ShieldCheck size={15} strokeWidth={1.5} /> Security
          </a>
        </header>

        <div className="flex-1 flex items-center px-6 md:px-10 lg:px-24">
          <div className="w-full max-w-xl">
            {stage === "intro" && (
              <div className="fade-up">
                <p className="font-body text-[11px] font-semibold tracking-[0.4em] uppercase text-[#2BC4B5] mb-6">An encrypted community &amp; creator platform</p>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-none text-white mb-6">
                  Sovereign systems,<br />shared in confidence.
                </h1>
                <p className="font-body text-lg font-medium text-white leading-snug [text-shadow:0_1px_8px_rgba(0,0,0,0.8)] max-w-md mb-6" data-testid="landing-backup-hook">
                  <span className="text-[#2BC4B5]">Your platform can ban you, shut down, or bury your reach overnight.</span> Sovereign Signal is encrypted, backed up, and built so that can't happen here.
                </p>
                <p className="font-body text-base font-medium text-zinc-100 leading-relaxed [text-shadow:0_1px_8px_rgba(0,0,0,0.8)] max-w-md mb-8">
                  Bring your ideas, businesses, communities, teachings, and creations into a private space where they can grow without surveillance, takedown, or algorithmic interference. Sovereign Signal is an encrypted community where conscious folks can share, collaborate, and build meaningful relationships in confidence. Together, we're creating new systems, supporting one another, and building the future we choose, not the one we're given.
                </p>

                <div className="space-y-3 mb-6 max-w-md" data-testid="landing-trust">
                  <div className="flex items-start gap-3">
                    <Lock size={16} className="text-[#2BC4B5] shrink-0 mt-0.5" strokeWidth={2} />
                    <p className="font-body text-sm font-medium text-zinc-100 leading-snug [text-shadow:0_1px_8px_rgba(0,0,0,0.8)]"><span className="text-white font-semibold">End-to-end encrypted.</span> Your posts and messages are scrambled on your device, so we can't read them, sell them, or hand them over.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <ShieldCheck size={16} className="text-[#2BC4B5] shrink-0 mt-0.5" strokeWidth={2} />
                    <p className="font-body text-sm font-medium text-zinc-100 leading-snug [text-shadow:0_1px_8px_rgba(0,0,0,0.8)]"><span className="text-white font-semibold">No de-platforming.</span> No algorithm and no corporate gatekeeper, so you won't be silenced here for your views.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <RefreshCw size={16} className="text-[#2BC4B5] shrink-0 mt-0.5" strokeWidth={2} />
                    <p className="font-body text-sm font-medium text-zinc-100 leading-snug [text-shadow:0_1px_8px_rgba(0,0,0,0.8)]"><span className="text-white font-semibold">Built to endure.</span> Sovereign &amp; decentralized by design: if we're ever forced offline, the network rebuilds and your community and content come with you.</p>
                  </div>
                </div>

                <a href="/security" data-testid="landing-verify-security" className="inline-flex items-center gap-1.5 text-[#2BC4B5] hover:text-white transition-colors font-body text-sm font-medium mb-10 group">
                  Don't take our word for it: here's exactly how our security works, limits included
                  <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                </a>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button onClick={() => navigate("/welcome")} data-testid="request-invite-button" className="bg-[#14A89A] text-[#0A0A0A] font-medium px-8 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body">
                    Request an invite <ArrowRight size={18} />
                  </button>
                  <button onClick={() => setStage("gate")} data-testid="enter-button" className="border border-[#14A89A]/50 text-[#14A89A] font-medium px-8 py-4 hover:bg-[#14A89A]/10 transition-colors font-body">
                    I have a key
                  </button>
                  <button onClick={() => setStage("login")} data-testid="member-login-button" className="border border-white/70 text-white font-semibold px-8 py-4 hover:bg-white/10 hover:border-white transition-colors font-body">
                    I'm a member
                  </button>
                </div>
              </div>
            )}

            {stage === "gate" && (
              <div className="fade-up max-w-sm">
                <button onClick={() => setStage("intro")} className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs mb-8 font-body"><ChevronLeft size={14}/> Back</button>
                <Lock className="text-[#14A89A] mb-5" strokeWidth={1.5} />
                <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-white mb-3">Present your signal key</h2>
                <p className="font-body text-sm text-zinc-400 mb-8">Entry is by invitation. Enter the personal key from your invite email. Don't have one? You can request an invite below.</p>
                <Field label="Signal Key" placeholder="••••••••" value={code} onChange={(e) => setCode(e.target.value)} data-testid="invite-code-input" />
                {error && <p className="text-red-400 text-sm mt-4 font-body" data-testid="gate-error">{error}</p>}
                <button onClick={verify} disabled={loading || !code} data-testid="verify-invite-button" className="mt-8 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
                  {loading ? <Loader2 className="animate-spin" size={18}/> : <>Verify <ArrowRight size={18}/></>}
                </button>
                <button onClick={() => navigate("/welcome")} data-testid="gate-request-invite-link" className="mt-6 text-[#14A89A] hover:text-[#2BC4B5] text-sm font-body w-full text-center transition-colors">Don't have a key? Request an invite</button>
                <button onClick={() => setStage("login")} className="mt-3 text-zinc-500 hover:text-white text-xs font-body w-full text-center">Already a member? Sign in</button>
              </div>
            )}

            {stage === "login" && (
              <form onSubmit={doLogin} className="fade-up max-w-sm">
                <button type="button" onClick={() => setStage("intro")} className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs mb-8 font-body"><ChevronLeft size={14}/> Back</button>
                <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-white mb-8">Welcome back</h2>
                <div className="space-y-6">
                  <Field label="Email" type="email" value={lEmail} onChange={(e)=>setLEmail(e.target.value)} data-testid="login-email-input" required />
                  <Field label="Password" type="password" value={lPass} onChange={(e)=>setLPass(e.target.value)} data-testid="login-password-input" required />
                </div>
                {error && <p className="text-red-400 text-sm mt-4 font-body" data-testid="login-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="login-submit-button" className="mt-8 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
                  {loading ? <Loader2 className="animate-spin" size={18}/> : <>Sign in <ArrowRight size={18}/></>}
                </button>
                <button type="button" onClick={() => { setError(""); setStage("forgot"); }} data-testid="forgot-password-link" className="mt-5 text-zinc-500 hover:text-[#14A89A] text-xs font-body w-full text-center transition-colors">Forgot your password?</button>
                <button type="button" onClick={() => setStage("gate")} className="mt-3 text-zinc-500 hover:text-white text-xs font-body w-full text-center">Have an invite? Apply for membership</button>
              </form>
            )}

            {stage === "mfa" && (
              <form onSubmit={doMfaVerify} className="fade-up max-w-sm">
                <button type="button" onClick={() => { setError(""); setMfaCode(""); setStage("login"); }} className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs mb-8 font-body"><ChevronLeft size={14}/> Back</button>
                <ShieldCheck className="text-[#14A89A] mb-5" strokeWidth={1.5} />
                <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-white mb-3">Enter your code</h2>
                <p className="font-body text-sm text-zinc-400 mb-8">Open your authenticator app and enter the current 6-digit code. Lost your app? Enter one of your backup codes instead.</p>
                <Field label="Authentication code" type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" value={mfaCode} onChange={(e)=>setMfaCode(e.target.value)} data-testid="mfa-code-input" autoFocus required />
                {error && <p className="text-red-400 text-sm mt-4 font-body" data-testid="mfa-error">{error}</p>}
                <button type="submit" disabled={loading || !mfaCode} data-testid="mfa-verify-button" className="mt-8 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
                  {loading ? <Loader2 className="animate-spin" size={18}/> : <>Verify &amp; sign in <ArrowRight size={18}/></>}
                </button>
              </form>
            )}

            {stage === "forgot" && (
              <form onSubmit={doReset} className="fade-up max-w-sm">
                <button type="button" onClick={() => setStage("login")} className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs mb-8 font-body"><ChevronLeft size={14}/> Back</button>
                <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-white mb-2">Recover access</h2>
                <p className="font-body text-sm text-zinc-400 mb-8">Because your account is end-to-end encrypted, your <span className="text-white">12-word recovery phrase</span> is the only way to reset your password. It restores your encrypted vault and sets a new password.</p>
                <div className="space-y-6">
                  <Field label="Email" type="email" value={fEmail} onChange={(e)=>setFEmail(e.target.value)} data-testid="forgot-email-input" required />
                  <label className="block">
                    <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">Recovery Phrase</span>
                    <textarea rows={3} placeholder="Enter your 12 words" value={fPhrase} onChange={(e)=>setFPhrase(e.target.value)} data-testid="forgot-phrase-input" className="mt-2 bg-transparent border border-white/15 rounded-none px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] w-full font-body resize-none" required />
                  </label>
                  <Field label="New Password" type="password" value={fNewPw} onChange={(e)=>setFNewPw(e.target.value)} data-testid="forgot-new-password" required />
                </div>
                {error && <p className="text-red-400 text-sm mt-4 font-body" data-testid="forgot-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="forgot-submit-button" className="mt-8 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
                  {loading ? <Loader2 className="animate-spin" size={18}/> : <>Reset & sign in <ArrowRight size={18}/></>}
                </button>
              </form>
            )}

            {stage === "apply" && (
              <form onSubmit={doApply} className="fade-up w-full max-w-lg py-10">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-body text-[10px] tracking-[0.4em] uppercase text-[#14A89A]">Key accepted · Welcome</p>
                  <p className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-500" data-testid="apply-step-indicator">Step {applyStep} of 2</p>
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-white mb-3" data-testid="apply-heading">What do you bring - and what are you here to find?</h2>
                {resumed && applyStep === 1 && (
                  <p className="font-body text-xs text-[#14A89A] mb-4" data-testid="apply-resumed-note">
                    Welcome back, your answers are saved. Re-enter a password to continue.
                  </p>
                )}

                {applyStep === 1 && (
                  <>
                    <p className="font-body text-sm text-zinc-300 mb-2 leading-relaxed" data-testid="apply-intro">Your key worked - this is where you tell us who you are. Share what you create, teach, or offer, and what you're hoping to find here. It takes about two minutes, and the Council takes it from there.</p>
                    <p className="font-body text-xs text-zinc-500 mb-8 leading-relaxed" data-testid="apply-intro-note">Come as you are, there's no wrong answer, and nothing here is permanent. You can edit anytime. Just like life 😊</p>
                    <div className="space-y-6">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <Field label="Name" value={form.name} onChange={upd("name")} data-testid="apply-name-input" required />
                        <Field label="Email" type="email" value={form.email} onChange={upd("email")} data-testid="apply-email-input" required />
                      </div>
                      <div>
                        <Field label="Password" type="password" value={form.password} onChange={upd("password")} data-testid="apply-password-input" required />
                        <span className="block mt-1.5 font-body text-[11px] text-zinc-600" data-testid="apply-password-helper">At least 8 characters.</span>
                      </div>
                      <label className="block">
                        <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">Your realms (up to 5)</span>
                        <div className="mt-2" data-testid="apply-category-select">
                          <DomainPicker value={form.domains} onChange={(d) => setForm({ ...form, domains: d })} />
                        </div>
                        <details className="mt-3 group" data-testid="apply-domain-help">
                          <summary className="cursor-pointer list-none font-body text-xs text-[#14A89A] hover:text-[#2BC4B5] transition-colors flex items-center gap-1.5 select-none">
                            <ArrowRight size={13} className="transition-transform group-open:rotate-90" /> How do I choose my realms?
                          </summary>
                          <div className="mt-3 font-body text-xs text-zinc-400 leading-relaxed space-y-2 border-l border-white/10 pl-3">
                            <p>Choose the realms your <span className="text-white">gift or work most reflects</span> - not just the business category they fall under. Pick up to five; the first you select becomes your <span className="text-white">primary realm</span>.</p>
                            <p><span className="text-white">Example:</span> if you're a professional artist, choose <span className="text-[#14A89A]">Art</span> - even though art is your business. Pick the realms that best capture the <span className="text-white">essence</span> of what you contribute.</p>
                            <p className="text-zinc-500">These are your profile realms. When you post, you'll also choose the realm that fits each individual post.</p>
                          </div>
                        </details>
                      </label>
                    </div>
                    {error && <p className="text-red-400 text-sm mt-4 font-body" data-testid="apply-error">{error}</p>}
                    <button type="button" onClick={goToStep2} data-testid="apply-continue-button" className="mt-8 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body">
                      Continue <ArrowRight size={18}/>
                    </button>
                  </>
                )}

                {applyStep === 2 && (
                  <>
                    <p className="font-body text-xs text-zinc-500 mb-8 leading-relaxed">A little about you and what you're here for. These become your profile, and you can edit anytime.</p>
                    <div className="space-y-6">
                      <div>
                        <Area label="Tell your story" placeholder="What you've created, and why it matters" helper="A sentence or two is plenty." value={form.bio} onChange={upd("bio")} data-testid="apply-bio-input" required />
                        <ChipRow testid="apply-bio-chips" chips={["I create", "I teach", "I'm building", "I help people", "I grow"]} onPick={(c) => setForm((f) => ({ ...f, bio: f.bio.trim() ? `${f.bio.trim()} ${c} ` : `${c} ` }))} />
                      </div>
                      <div>
                        <Area label="What you're seeking (optional)" placeholder="What you offer and what you're seeking: collaborators, skills, capital, exchange…" helper="Optional, a few words is plenty. You can add this anytime from your profile." value={form.seeking} onChange={upd("seeking")} data-testid="apply-seeking-input" />
                        <ChipRow testid="apply-seeking-chips" chips={["collaborators", "clients", "skills to learn", "capital", "community"]} onPick={(c) => setForm((f) => ({ ...f, seeking: f.seeking.trim() ? `${f.seeking.trim()}, ${c}` : c }))} />
                      </div>
                      <Field label="Link (optional)" placeholder="https://" value={form.link} onChange={upd("link")} data-testid="apply-link-input" />
                    </div>
                    {error && <p className="text-red-400 text-sm mt-4 font-body" data-testid="apply-error">{error}</p>}
                    <button type="submit" disabled={loading || form.domains.length === 0} data-testid="apply-submit-button" className="mt-8 w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
                      {loading ? <Loader2 className="animate-spin" size={18}/> : <>Finish My Profile <ArrowRight size={18}/></>}
                    </button>
                    <button type="button" onClick={() => { setError(""); setApplyStep(1); }} data-testid="apply-back-button" className="mt-4 text-zinc-500 hover:text-white text-xs font-body w-full text-center transition-colors">Back</button>
                    <p className="mt-3 text-center font-body text-xs text-zinc-500" data-testid="apply-approval-note">Please allow up to 24 hours for approval.</p>
                  </>
                )}
              </form>
            )}

            {stage === "phrase" && phrase && (
              <div className="fade-up w-full max-w-md py-10" data-testid="register-recovery-phrase">
                <p className="font-body text-[10px] tracking-[0.4em] uppercase text-[#14A89A] mb-3">Application Received</p>
                <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-white mb-2">Save your recovery phrase</h2>
                <p className="font-body text-sm text-zinc-400 mb-6">Your account is end-to-end encrypted. These 12 words are the <span className="text-white">only</span> way to restore your encrypted vault if you lose your password or device. Store them offline - they can never be recovered for you. <span className="text-zinc-500">You can view them anytime under Edit Profile.</span></p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {phrase.split(" ").map((w, i) => (
                    <div key={i} className="bg-[#0A0A0A] border border-white/10 px-2 py-2 text-center">
                      <span className="text-zinc-600 text-[10px] mr-1">{i + 1}</span>
                      <span className="font-body text-sm text-[#14A89A]">{w}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => navigator.clipboard.writeText(phrase)} className="text-zinc-400 hover:text-white text-xs font-body mb-6">Copy to clipboard</button>
                <label className="flex items-start gap-2 mb-6 cursor-pointer">
                  <input type="checkbox" checked={phraseAck} onChange={(e) => setPhraseAck(e.target.checked)} data-testid="register-phrase-ack" className="mt-1 accent-[#14A89A]" />
                  <span className="font-body text-xs text-zinc-300">I have safely stored my recovery phrase.</span>
                </label>
                <button onClick={() => setShowPhraseConfirm(true)} disabled={!phraseAck} data-testid="register-phrase-continue" className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
                  Continue <ArrowRight size={18} />
                </button>

                {showPhraseConfirm && (
                  <div className="fixed inset-0 z-[120] bg-black/85 flex items-center justify-center p-6" data-testid="phrase-confirm-modal">
                    <div className="bg-[#121212] border border-[#14A89A]/30 max-w-sm w-full p-6 fade-up">
                      <ShieldCheck className="text-[#14A89A] mb-4" strokeWidth={1.5} />
                      <h3 className="font-heading text-xl text-white mb-3">Have you truly saved your 12 words?</h3>
                      <p className="font-body text-sm text-zinc-400 mb-6 leading-relaxed">
                        This phrase is the <span className="text-white">only</span> way back into your encrypted account. We can never recover it for you. If you lose it and forget your password, your vault is gone forever.
                      </p>
                      <div className="flex flex-col gap-3">
                        <button
                          onClick={() => { if (pendingSession) setSession(pendingSession.token, pendingSession.user); navigate("/pending"); }}
                          data-testid="phrase-confirm-yes"
                          className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-3.5 hover:bg-[#2BC4B5] transition-colors font-body"
                        >
                          Yes, I've saved them
                        </button>
                        <button
                          onClick={() => setShowPhraseConfirm(false)}
                          data-testid="phrase-confirm-back"
                          className="w-full border border-white/15 text-white font-medium px-6 py-3.5 hover:bg-white/5 transition-colors font-body"
                        >
                          Go back &amp; save them
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {stage === "intro" && (
          <footer data-testid="landing-footer" className="px-6 md:px-10 lg:px-24 pb-8 pt-4">
            <TrustBadges className="max-w-xl border-t border-white/10 pt-6" />
          </footer>
        )}
      </div>
    </div>
  );
}
